import {
    ActionIcon,
    Badge,
    Button,
    Combobox,
    Group,
    InputBase,
    Stack,
    Text,
    useCombobox,
} from '@mantine/core'
import {IconEdit, IconPlus, IconX} from '@tabler/icons-react'
import {useState} from 'react'

import {isAdmin} from 'src/auth/is-admin'
import {useCurrentUser} from 'src/auth/use-current-user'

import {InlineCreateCustomerContactDialog} from './inline-create-customer-contact-dialog'
import type {CustomerContact} from './schemas'
import {useCustomer} from './use-customers'

export type CustomerPocSelectorProps = {
    customerId: string | null
    value: string | null
    label?: string
    placeholder?: string
    error?: string
    disabled?: boolean
    onChange: (contactId: string | null) => void
}

/**
 * Clearable single-select customer point-of-contact selector.
 *
 * Loads one customer's contacts, lets the caller pick one (or clear it), and
 * supports inline creation of a new contact that is selected immediately.
 * Exposes disabled/loading/error states for embedding in wizard steps.
 */
export function CustomerPocSelector({
    customerId,
    value,
    label = 'Customer point of contact',
    placeholder = 'Select a contact',
    error,
    disabled = false,
    onChange,
}: CustomerPocSelectorProps) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })
    const [createDialogOpened, setCreateDialogOpened] = useState(false)
    const [editDialogOpened, setEditDialogOpened] = useState(false)
    const canEditContact = isAdmin(useCurrentUser())

    const enabled = Boolean(customerId) && !disabled
    const {data: customer, isPending, isError} = useCustomer(customerId ?? '', {enabled})
    const contacts = customer?.contacts ?? []
    const selectedContact = value ? contacts.find((contact) => contact.id === value) : undefined

    function handleSelect(contactId: string) {
        onChange(contactId)
        combobox.closeDropdown()
    }

    function handleClear() {
        onChange(null)
        combobox.closeDropdown()
    }

    function handleCreated(contact: CustomerContact) {
        onChange(contact.id)
    }

    const loadingState = isPending && enabled
    const empty = !loadingState && contacts.length === 0

    return (
        <>
            <Stack gap="xs">
                <Combobox
                    store={combobox}
                    onOptionSubmit={(optionValue) => {
                        if (optionValue === '__create__') {
                            setCreateDialogOpened(true)
                            combobox.closeDropdown()
                            return
                        }
                        if (optionValue === '__clear__') {
                            handleClear()
                            return
                        }
                        handleSelect(optionValue)
                    }}
                >
                    <Combobox.Target>
                        <InputBase
                            label={label}
                            placeholder={placeholder}
                            value={selectedContact?.name ?? ''}
                            error={error}
                            disabled={disabled || loadingState || !customerId}
                            readOnly
                            rightSection={
                                loadingState ? (
                                    '…'
                                ) : selectedContact ? (
                                    <ActionIcon
                                        size="sm"
                                        variant="transparent"
                                        aria-label="Clear customer point of contact"
                                        onClick={handleClear}
                                        disabled={disabled}
                                    >
                                        <IconX size={14} />
                                    </ActionIcon>
                                ) : undefined
                            }
                            onClick={() => combobox.toggleDropdown()}
                        />
                    </Combobox.Target>

                    <Combobox.Dropdown>
                        <Combobox.Options>
                            {selectedContact ? (
                                <Combobox.Option value="__clear__">
                                    <Text size="sm" c="dimmed">
                                        Clear selection
                                    </Text>
                                </Combobox.Option>
                            ) : null}
                            {contacts.map((contact) => (
                                <Combobox.Option
                                    key={contact.id}
                                    value={contact.id}
                                    onMouseDown={(event) => event.preventDefault()}
                                >
                                    <Group gap="xs" wrap="nowrap">
                                        <Text size="sm">{contact.name}</Text>
                                        {contact.title ? (
                                            <Text size="xs" c="dimmed">
                                                {contact.title}
                                            </Text>
                                        ) : null}
                                        {contact.isAssigned ? (
                                            <Badge size="xs" variant="light" color="grape">
                                                Assigned
                                            </Badge>
                                        ) : null}
                                    </Group>
                                </Combobox.Option>
                            ))}
                            <Combobox.Option
                                value="__create__"
                                onMouseDown={(event) => event.preventDefault()}
                            >
                                <Group gap="xs" wrap="nowrap">
                                    <IconPlus size={14} />
                                    <Text size="sm">Add new contact</Text>
                                </Group>
                            </Combobox.Option>
                            {empty ? <Combobox.Empty>No contacts found</Combobox.Empty> : null}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>

                {selectedContact && canEditContact && !disabled ? (
                    <Group justify="flex-end">
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            leftSection={<IconEdit size={14} />}
                            onClick={() => setEditDialogOpened(true)}
                        >
                            Edit contact
                        </Button>
                    </Group>
                ) : null}

                {isError && enabled ? (
                    <Text size="sm" c="red">
                        Failed to load customer contacts
                    </Text>
                ) : null}
            </Stack>

            {customerId ? (
                <InlineCreateCustomerContactDialog
                    customerId={customerId}
                    opened={createDialogOpened}
                    onClose={() => setCreateDialogOpened(false)}
                    onCreated={handleCreated}
                />
            ) : null}
            {customerId && selectedContact ? (
                <InlineCreateCustomerContactDialog
                    customerId={customerId}
                    contact={selectedContact}
                    opened={editDialogOpened}
                    onClose={() => setEditDialogOpened(false)}
                />
            ) : null}
        </>
    )
}
